import React, { useCallback, useEffect, useState } from 'react';
import { Box, ButtonBase, Checkbox, Chip, IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BasicTable from '../../../components/BasicTable/BasicTable';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import ListToolbar from '../../../components/ListToolbar/ListToolbar';
import RowActionsMenu from '../../../components/RowActionsMenu/RowActionsMenu';
import FilteredEmptyState from '../../../components/FilteredEmptyState';
import SegmentedControl from '../../../components/SegmentedControl';
import ChildPaymentsModal from './ChildPaymentsModal';
import PaymentPanel from './Payments/PaymentPanel';
import PayerBalancesTable, { payerKey } from './PayerBalancesTable';
import BalanceCards from './Debts/BalanceCards';
import { LastPaymentCell, MonthChips, OwedCell, PayerCell } from './Debts/DebtCells';
import { ITableHeadings } from '../../../common/interfaces/common.interface';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { contractHref } from '../../../common/constants/routes.constants';
import { useListUrlState } from '../../../utilities/hooks/useListUrlState';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { downloadBlob } from '../../../utilities/helper/download.helper';
import { formatMoney } from '../../../utilities/helper/money.helper';
import {
  BalanceBucketFilter,
  BalanceBucketsDto,
  ChildBalanceDto,
  ChildBalancePageDto,
  PayerBalanceDto,
  PayerBalancePageDto
} from '../../../common/interfaces/invoicing.interface';
import { exportBalances, getBalances, getBalancesByPayer } from '../../../api/invoicing.api';
import { getGroupsWithLocationName } from '../../../api/kindergarten.api';
import { useLatestRequest } from '../../../utilities/hooks/useLatestRequest';

/** The list's filters in the URL. */
const FILTER = { group: 'group', bucket: 'bucket', byPayer: 'byPayer' };
const BUCKETS: BalanceBucketFilter[] = ['all', 'late30', 'late', 'notdue', 'credit'];
const white = (theme: Theme) => theme.palette.common.white;

interface BalancesTabProps {
  kindergartenId: string;
  /** Only used to build links into a child; the debts themselves do not need it. */
  kindergartenName?: string;
}

/** The family version of the same draft: one mail covering every child they pay for. */
const familyReminderMailto = (payer: PayerBalanceDto): string => {
  const names = payer.children.map(child => child.childFullName).join(', ');
  const subject = `Neizmirene obaveze - ${names}`;
  const perChild = payer.children
    .filter(child => child.outstanding > 0)
    .map(child => `- ${child.childFullName}: ${formatMoney(child.outstanding, { withCurrency: true })}`)
    .join('\n');
  const body = [
    `Poštovani/a ${payer.payerFullName ?? ''},`,
    '',
    `Evidentirano je neizmireno dugovanje u ukupnom iznosu od ${formatMoney(payer.outstanding, { withCurrency: true })}:`,
    perChild,
    payer.oldestDueDate ? `\nNajstariji nenaplaćeni račun dospeo je ${payer.oldestDueDate}.` : '',
    '',
    'Hvala unapred.'
  ]
    .filter(Boolean)
    .join('\n');
  return `mailto:${payer.payerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/**
 * A prefilled email to the payer. The platform sends nothing itself, so this hands the
 * admin a draft in their own mail client rather than pretending to be a reminder system.
 */
const reminderMailto = (balance: ChildBalanceDto): string => {
  const subject = `Neizmirene obaveze - ${balance.childFullName}`;
  const body = [
    `Poštovani/a ${balance.payerFullName ?? ''},`,
    '',
    `Na ime ${balance.childFullName} evidentirano je neizmireno dugovanje u iznosu od ${formatMoney(balance.outstanding, {
      withCurrency: true
    })}.`,
    balance.oldestDueDate ? `Najstariji nenaplaćeni račun dospeo je ${balance.oldestDueDate}.` : '',
    '',
    'Hvala unapred.'
  ]
    .filter(Boolean)
    .join('\n');
  return `mailto:${balance.payerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/** "Duguje ↓": the list is ordered by how late and how much, and the arrow says so. */
const OwedHeading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.5 }}>
      {t('balances.columns.owes')}
      <Box component="span" aria-hidden sx={{ color: 'primary.main' }}>
        ↓
      </Box>
    </Box>
  );
};

/**
 * Who owes, how late they are, and when they last paid.
 *
 * The cards on top are both the totals and the filter. The server filters, orders and
 * pages: the longest late first, then the largest debt, so page one is whoever is worth
 * calling first. Sorting here instead would only reorder whichever page the server sent.
 */
const BalancesTab: React.FC<BalancesTabProps> = ({ kindergartenId, kindergartenName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = useListUrlState();
  const { search, page, perPage } = list;
  const groupIds = list.values(FILTER.group);
  const rawBucket = list.values(FILTER.bucket)[0] as BalanceBucketFilter | undefined;
  const bucket: BalanceBucketFilter = rawBucket && BUCKETS.includes(rawBucket) ? rawBucket : 'all';
  const byPayer = list.flag(FILTER.byPayer);

  const [balances, setBalances] = useState<ChildBalanceDto[]>([]);
  const [payers, setPayers] = useState<PayerBalanceDto[]>([]);
  const [buckets, setBuckets] = useState<BalanceBucketsDto | null>(null);
  const [total, setTotal] = useState(0);
  const [loader, setLoader] = useState(true);
  const beginLoad = useLatestRequest();
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [history, setHistory] = useState<ChildBalanceDto | null>(null);
  const [paying, setPaying] = useState<ChildBalanceDto | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  const params = { search: search || undefined, groupIds: groupIds.length ? groupIds : undefined, bucket };

  const load = useCallback(async () => {
    const isCurrent = beginLoad();
    setLoader(true);
    try {
      // the two views page differently - one by child, one by family - so they are
      // separate requests rather than the same rows regrouped on the client
      const query = { page, perPage, ...params };
      const result = byPayer ? await getBalancesByPayer(kindergartenId, query) : await getBalances(kindergartenId, query);
      if (!isCurrent()) return;
      if (byPayer) setPayers((result.results as PayerBalancePageDto | undefined)?.items ?? []);
      else setBalances((result.results as ChildBalancePageDto | undefined)?.items ?? []);
      setBuckets(result.results?.buckets ?? null);
      setTotal(result.paginationInfo?.totalItems ?? 0);
    } catch {
      if (isCurrent()) createNotification(MESSAGE_TYPE.ERROR, t('balances.loadFailed'));
    } finally {
      if (isCurrent()) setLoader(false);
    }
    // the group list is rebuilt from the URL on every render; its joined form is the dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindergartenId, page, perPage, search, groupIds.join(), bucket, byPayer]);

  useEffect(() => {
    load();
  }, [load]);

  // a choice belongs to the rows it was made on; another view or card starts clean
  useEffect(() => setChosen(new Set()), [byPayer, bucket]);

  useEffect(() => {
    getGroupsWithLocationName(kindergartenId)
      .then(result => setGroups((result.results ?? []).map(group => ({ id: group.id, name: group.name }))))
      .catch(() => createNotification(MESSAGE_TYPE.ERROR, t('groups.listLoadFailed')));
  }, [kindergartenId, t]);

  const clearFilters = () => list.clearFilters([FILTER.group]);
  const filtered = !!search || !!groupIds.length;
  const emptyState = filtered ? (
    <FilteredEmptyState onClear={clearFilters} />
  ) : (
    <Typography sx={{ fontSize: 14, color: 'text.secondary', py: 2 }}>{t(`balances.empty.${bucket}`)}</Typography>
  );

  const rowKeys = byPayer ? payers.filter(payer => payer.payerId).map(payerKey) : balances.map(balance => balance.childId);
  const allChosen = rowKeys.length > 0 && rowKeys.every(key => chosen.has(key));
  const toggle = (key: string) =>
    setChosen(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const togglePage = () =>
    setChosen(prev => {
      const next = new Set(prev);
      rowKeys.forEach(key => (allChosen ? next.delete(key) : next.add(key)));
      return next;
    });

  const downloadCsv = async (onlyChosen: boolean) => {
    setExporting(true);
    try {
      const view = byPayer ? 'payer' : 'child';
      const ids = Array.from(chosen);
      const blob = await exportBalances(kindergartenId, {
        ...params,
        view,
        ...(onlyChosen ? (byPayer ? { payerIds: ids } : { childIds: ids }) : {})
      });
      downloadBlob(blob, byPayer ? 'dugovanja-po-platiocu.csv' : 'dugovanja.csv');
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('balances.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const selectAll = (
    <Checkbox
      size="small"
      checked={allChosen}
      indeterminate={!allChosen && rowKeys.some(key => chosen.has(key))}
      onChange={togglePage}
      slotProps={{ input: { 'aria-label': t('balances.selectPage') } }}
    />
  );

  const headings: ITableHeadings[] = [
    { id: 0, value: '', label: '', sortable: false, width: 48, content: selectAll },
    { id: 1, value: '', label: 'common.child', sortable: false, width: '20%' },
    // the person actually chased for the money, which is never the child
    { id: 2, value: '', label: 'contracts.payer', sortable: false, width: '20%' },
    { id: 3, value: '', label: 'balances.columns.unpaid', sortable: false },
    { id: 4, value: '', label: 'balances.columns.owes', sortable: false, align: 'right', width: 150, content: <OwedHeading /> },
    { id: 5, value: '', label: 'balances.columns.lastPayment', sortable: false, width: 130 },
    { id: 6, value: '', label: '', sortable: false, width: 56 }
  ];

  return (
    <Box sx={{ pb: chosen.size ? 10 : 0 }}>
      <BalanceCards buckets={buckets} value={bucket} onChange={next => list.setValues(FILTER.bucket, next === 'all' ? [] : [next])} />

      <Box sx={{ border: 1, borderColor: 'line', borderRadius: 4 }}>
        <ListToolbar
          search={{ value: search, onChange: list.setSearch, placeholder: t('balances.searchChildOrPayer') }}
          filters={[
            {
              kind: 'multi',
              key: FILTER.group,
              label: t('common.group'),
              options: groups.map(group => ({ value: group.id, label: group.name })),
              selected: groupIds,
              onChange: values => list.setValues(FILTER.group, values)
            }
          ]}
          extra={
            <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 'auto !important' }}>
              {!chosen.size && (
                <GenericButton
                  buttonType={GenericButtonTypeEnum.Secondary}
                  variant="outlined"
                  label={t('balances.downloadCsv')}
                  onAsyncAction={() => downloadCsv(false)}
                  disabled={exporting}
                />
              )}
              {/* not a filter but two ways of reading the same debts, so it sits apart from them */}
              <SegmentedControl
                ariaLabel={t('balances.view.label')}
                value={byPayer ? 'payer' : 'child'}
                onChange={value => list.setFlag(FILTER.byPayer, value === 'payer')}
                width={260}
                options={[
                  { value: 'child', label: t('balances.view.byChild') },
                  { value: 'payer', label: t('balances.view.byPayer') }
                ]}
              />
              <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>{t('balances.amountsInRsd')}</Typography>
            </Stack>
          }
          onClearAll={clearFilters}
        />

        {byPayer ? (
          <PayerBalancesTable
            kindergartenId={kindergartenId}
            kindergartenName={kindergartenName}
            payers={payers}
            loader={loader}
            {...list.tableProps(total)}
            buildMailto={familyReminderMailto}
            emptyState={emptyState}
            selectAll={selectAll}
            chosen={chosen}
            onToggle={toggle}
          />
        ) : (
          <BasicTable headings={headings} loader={loader} {...list.tableProps(total)} emptyState={emptyState}>
            {balances.map(balance => (
              // the row records a payment, which is what a debt row is opened for; the
              // history is in the row's menu
              <TableRow
                key={balance.childId}
                hover
                tabIndex={0}
                selected={chosen.has(balance.childId)}
                onClick={() => setPaying(balance)}
                onKeyDown={event => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setPaying(balance);
                  }
                }}
                sx={{ cursor: 'pointer', verticalAlign: 'top' }}
              >
                <TableCell padding="checkbox" onClick={event => event.stopPropagation()}>
                  <Checkbox
                    size="small"
                    checked={chosen.has(balance.childId)}
                    onChange={() => toggle(balance.childId)}
                    slotProps={{ input: { 'aria-label': t('balances.select', { name: balance.childFullName }) } }}
                  />
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{balance.childFullName}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {[balance.groupName, balance.locationName].filter(Boolean).join(' · ') || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {balance.payerFullName ? (
                    <PayerCell name={balance.payerFullName} email={balance.payerEmail} />
                  ) : (
                    // not cosmetic: without a payer the child cannot be invoiced at all
                    <Chip size="small" color="warning" variant="outlined" label={t('contracts.noPayer')} />
                  )}
                </TableCell>
                <TableCell>
                  <MonthChips invoices={balance.openInvoices} credit={balance.credit} />
                </TableCell>
                <TableCell>
                  <OwedCell
                    outstanding={balance.outstanding}
                    overdueAmount={balance.overdueAmount}
                    daysOverdue={balance.daysOverdue}
                    credit={balance.credit}
                    invoices={balance.openInvoices}
                  />
                </TableCell>
                <TableCell>
                  <LastPaymentCell payment={balance.lastPayment} />
                </TableCell>
                <TableCell>
                  <RowActionsMenu
                    name={balance.childFullName}
                    actions={[
                      { label: t('payments.record'), onClick: () => setPaying(balance) },
                      { label: t('balances.paymentHistory'), onClick: () => setHistory(balance) },
                      {
                        // opens the admin's own mail client with the debt already written out;
                        // the platform sends no reminder of its own
                        label: t('balances.emailPayer', { name: balance.payerFullName }),
                        hidden: !balance.payerEmail || balance.outstanding <= 0,
                        onClick: () => window.open(reminderMailto(balance), '_blank', 'noopener')
                      },
                      { label: t('balances.openContract'), onClick: () => navigate(contractHref(kindergartenId, balance.childId)) }
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </BasicTable>
        )}
      </Box>

      {chosen.size > 0 && (
        <Box
          role="region"
          aria-label={t('balances.selection.ariaLabel')}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            bgcolor: 'primary.main',
            color: white,
            borderRadius: 4,
            boxShadow: 6,
            px: 2.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Typography sx={{ fontSize: 14, color: white, mr: 1 }}>{t('balances.selection.selected', { count: chosen.size })}</Typography>
          <ButtonBase
            onClick={() => downloadCsv(true)}
            disabled={exporting}
            sx={{ bgcolor: white, color: 'primary.main', borderRadius: 2, px: 1.75, height: 36, fontSize: 14, fontWeight: 500 }}
          >
            {t('balances.selection.csv')}
          </ButtonBase>
          <IconButton aria-label={t('balances.selection.clear')} onClick={() => setChosen(new Set())} sx={{ color: white }}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {paying && (
        <PaymentPanel
          kindergartenId={kindergartenId}
          childId={paying.childId}
          childName={paying.childFullName}
          payerName={paying.payerFullName ?? undefined}
          onClose={() => setPaying(null)}
          onRecorded={load}
        />
      )}
      {history && (
        <ChildPaymentsModal
          kindergartenId={kindergartenId}
          childId={history.childId}
          childName={history.childFullName}
          outstanding={history.outstanding}
          onClose={() => setHistory(null)}
          onChanged={load}
        />
      )}
    </Box>
  );
};

export default BalancesTab;
